import axios, {
  AxiosHeaders,
  AxiosResponseTransformer,
  CanceledError,
  InternalAxiosRequestConfig,
  ResponseType,
} from 'axios';
import { API_V1 } from '../constants';
import { tokenGetter } from './auth';
import { isMiddlewareAuthRejection, isMiddlewareAuthRejectionBinaryBody } from './authErrors';
import delegationState from './delegation';
import { ROUTES } from './routes';
import { getSessionEpoch } from './sessionBoundary';

// Wraps a per-call transformResponse so it short-circuits on HTTP error
// responses (4xx/5xx). The custom transformers in this app assume a
// success-shaped body and call `.map` / read `.data`; an error body like
// `{"detail":"Unauthorized"}` would throw a TypeError inside the transformer
// and *replace* the real axios error — leaving the alert layer with no status,
// no URL, and an opaque "JSON.parse(...).map is not a function" message.
//
// On non-2xx, parse the JSON (so `error.response.data` is structured) and
// return the body untransformed. Axios then rejects with its native error and
// the alert layer can format it properly.
export const safeTransformResponse = (
  transformer: AxiosResponseTransformer
): AxiosResponseTransformer => {
  return function safeTransformer(this: InternalAxiosRequestConfig, data, headers, status) {
    if (status != null && status >= 400) {
      if (typeof data === 'string') {
        try {
          return JSON.parse(data);
        } catch {
          return data;
        }
      }
      return data;
    }
    return transformer.call(this, data, headers, status);
  };
};

export const wrapTransformResponse = (
  transformResponse: InternalAxiosRequestConfig['transformResponse']
): InternalAxiosRequestConfig['transformResponse'] => {
  if (!transformResponse) return undefined;
  if (Array.isArray(transformResponse)) {
    return transformResponse.map(safeTransformResponse);
  }
  return safeTransformResponse(transformResponse);
};

export const apiAxiosV1 = axios.create({
  baseURL: `${API_V1}`,
  headers: {
    'Content-Type': 'application/json',
  },
});

const AXIOS_INSTANCES = {
  v1: apiAxiosV1,
};

// Set on a request config by retryOnceWithFreshToken so the replay carries the
// freshly minted token and is never retried a second time. axios keeps unknown
// config keys when it merges a replayed config.
type AuthRetryConfig = InternalAxiosRequestConfig & {
  _authRetried?: boolean;
  _retryToken?: string;
  // The session (see sessionBoundary.ts) the request was issued in. A request is
  // never completed or replayed with credentials from a later session.
  _sessionEpoch?: number;
};

apiAxiosV1.interceptors.request.use(async (config) => {
  if (config.signal?.aborted) throw new CanceledError();
  const authConfig = config as AuthRetryConfig;
  // A replay keeps the epoch of the request it replays; anything else is issued now.
  const sessionEpoch = (authConfig._sessionEpoch ??= getSessionEpoch());
  const token = authConfig._retryToken ?? (await tokenGetter.getToken());
  if (config.signal?.aborted) throw new CanceledError();
  // The session ended while the token was being fetched: the token may belong to
  // the next account, so this request must not leave carrying it.
  if (sessionEpoch !== getSessionEpoch()) throw new CanceledError();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    // Core 3 returns null only for a genuinely signed-out session and throws a
    // ClerkOfflineError for connectivity failures. Never turn null into the
    // invalid credential `Bearer null`; allow the API to handle an anonymous
    // request while offline failures continue to reject before dispatch.
    delete config.headers.Authorization;
  }
  const actingAsProviderId = delegationState.getActingAsProviderId();
  if (actingAsProviderId) {
    config.headers['X-Acting-As-Provider'] = actingAsProviderId.toString();
  } else {
    // A replayed config still carries the header it was first sent with.
    delete config.headers['X-Acting-As-Provider'];
  }
  if (!config.headers['Content-Type'] && config.method !== 'get') {
    config.headers['Content-Type'] = 'application/json';
  }
  return config;
});

/**
 * Response-error handler for apiAxiosV1. When the API rejects a request because
 * the Bearer token had already expired (the cached Clerk token can be stale), it
 * mints a fresh token and replays the request once. Anything else is rethrown
 * unchanged.
 *
 * Only the auth middleware's own rejection is replayed: it is the first
 * middleware, so the request never reached a handler and replaying a POST,
 * PATCH, PUT or DELETE cannot repeat a side effect. Handler and guard 401s carry
 * other details and are never retried. A Blob or ArrayBuffer error body (a
 * `responseType: 'blob'` request) is decoded to read the same detail; a body
 * that cannot be decoded is not retried.
 */
export const retryOnceWithFreshToken = async (error: unknown) => {
  if (!axios.isAxiosError(error) || !error.config || error.response?.status !== 401) {
    throw error;
  }
  const config = error.config as AuthRetryConfig;
  if (config._authRetried) throw error;

  // A request sent without a token is never retried.
  const sent = String(AxiosHeaders.from(config.headers).get('Authorization') ?? '');
  if (!sent.startsWith('Bearer ')) throw error;
  const sentToken = sent.slice(7);

  // Only the auth middleware's own rejection is replayed. A JSON body is already
  // parsed; a binary one is decoded first. Either way the detail decides.
  if (
    !isMiddlewareAuthRejection(error) &&
    !(await isMiddlewareAuthRejectionBinaryBody(error.response.data))
  ) {
    throw error;
  }
  if (config.signal?.aborted) throw error;
  // The request belongs to a session that has ended: replaying it under the
  // current session's credentials would run one account's action as another.
  if (config._sessionEpoch !== undefined && config._sessionEpoch !== getSessionEpoch()) {
    throw error;
  }

  let fresh: string | null;
  try {
    // If a refresh already landed (a late 401 from a fan-out), use it instead of
    // minting another token.
    const cached = await tokenGetter.getToken();
    fresh = cached && cached !== sentToken ? cached : await tokenGetter.getFreshToken();
  } catch {
    throw error;
  }
  if (!fresh || fresh === sentToken) throw error;
  if (config._sessionEpoch !== undefined && config._sessionEpoch !== getSessionEpoch()) {
    throw error;
  }

  config._authRetried = true;
  config._retryToken = fresh;
  // axios already replaced config.data with the serialized body on the first
  // dispatch. Running a caller's transformRequest again would throw or
  // double-encode it, so the replay sends the body as it is.
  config.transformRequest = [(data) => data];
  return apiAxiosV1.request(config);
};

apiAxiosV1.interceptors.response.use(undefined, retryOnceWithFreshToken);

export const setPathParams = (url: string, params: { [key: string]: string }) => {
  return Object.entries(params).reduce((acc, [key, value]) => {
    return acc.replace(`{${key}}`, value);
  }, url);
};

interface ServiceCallParams {
  method: InternalAxiosRequestConfig['method'];
  route: keyof typeof ROUTES;
  data?: InternalAxiosRequestConfig['data'];
  params?: InternalAxiosRequestConfig['params'];
  pathParams?: { [key: string]: string };
  headers?: InternalAxiosRequestConfig['headers'];
  version?: 'v1';
  transformRequest?: InternalAxiosRequestConfig['transformRequest'];
  transformResponse?: InternalAxiosRequestConfig['transformResponse'];
  responseType?: ResponseType;
  timeout?: number;
  signal?: AbortSignal;
}

type GetParams = Omit<ServiceCallParams, 'method' | 'route'> & {
  sort?: {
    sortBy: string;
    page: number;
    pageSize: number;
  };
  filter?: { [key: string]: string };
};

export const handleServiceCall = async ({
  method,
  route,
  data,
  params,
  pathParams,
  headers,
  transformRequest,
  version = 'v1',
  transformResponse,
  responseType = 'json',
  timeout,
  signal,
}: ServiceCallParams) => {
  const response = await AXIOS_INSTANCES[version].request({
    method,
    url: setPathParams(
      ROUTES[route][method as keyof (typeof ROUTES)[typeof route]],
      pathParams || {}
    ),
    data,
    params,
    headers,
    transformRequest,
    transformResponse: wrapTransformResponse(transformResponse),
    responseType,
    timeout,
    signal,
  });
  return response.data;
};

export const getData = async <T>(route: keyof typeof ROUTES, options?: GetParams): Promise<T> => {
  if (options?.sort) {
    const { sortBy, page, pageSize } = options.sort;
    options.params = {
      ...options.params,
      page: page.toString(),
      pageSize: pageSize.toString(),
      sortBy,
      sortDir: options.sort[sortBy as keyof typeof options.sort] === 1 ? 'asc' : 'desc',
    };
  }
  if (options?.filter) {
    options.params = {
      ...options.params,
      ...Object.entries(options.filter)
        .filter(([, value]) => value !== 'all')
        .reduce((acc: { [key: string]: string }, [key, value]) => {
          acc[key] = encodeURIComponent(value);
          return acc;
        }, {}),
    };
  }
  return handleServiceCall({
    method: 'get',
    route: route,
    ...options,
  });
};

export const postData = async <T>(
  route: keyof typeof ROUTES,
  options?: Omit<ServiceCallParams, 'method' | 'route'> & {
    params?: { [key: string]: string | number };
  }
): Promise<T> => {
  return handleServiceCall({
    method: 'post',
    route: route,
    ...options,
  });
};

export const patchData = async <T>(
  route: keyof typeof ROUTES,
  options?: Omit<ServiceCallParams, 'method' | 'route'>
): Promise<T> => {
  return handleServiceCall({
    method: 'patch',
    route: route,
    ...options,
  });
};

type DefaultServiceResponse = Awaited<ReturnType<typeof handleServiceCall>>;

export const deleteData = async <T = DefaultServiceResponse>(
  route: keyof typeof ROUTES,
  options?: Omit<ServiceCallParams, 'method' | 'route'>
): Promise<T> => {
  return handleServiceCall({
    method: 'delete',
    route: route,
    ...options,
  });
};

export const putData = async <T = DefaultServiceResponse>(
  route: keyof typeof ROUTES,
  options?: Omit<ServiceCallParams, 'method' | 'route'>
): Promise<T> => {
  return handleServiceCall({
    method: 'put',
    route: route,
    ...options,
  });
};
